/**
 * Date presets for the Webstore Analytics tab.
 *
 * UTC throughout, because the product-analytics views bucket by UTC day. Building
 * these in local time would put the first and last day of every range in the wrong
 * bucket for anyone outside UTC — a silent off-by-one, not a visible error.
 */

export type DateRangePreset = "last-7" | "last-30" | "this-month" | "prev-month" | "custom";

export interface DateRange {
  startDate: string;
  endDate: string;
}

export function formatToISODate(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function getDateRangeForPreset(preset: DateRangePreset): DateRange {
  const now = new Date();

  switch (preset) {
    case "last-7": {
      const start = new Date(now);
      start.setUTCDate(now.getUTCDate() - 6);
      return { startDate: formatToISODate(start), endDate: formatToISODate(now) };
    }
    case "this-month": {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      return { startDate: formatToISODate(start), endDate: formatToISODate(now) };
    }
    case "prev-month": {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      // Day 0 of this month is the last day of the previous one.
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      return { startDate: formatToISODate(start), endDate: formatToISODate(end) };
    }
    case "last-30":
    default: {
      const start = new Date(now);
      start.setUTCDate(now.getUTCDate() - 29);
      return { startDate: formatToISODate(start), endDate: formatToISODate(now) };
    }
  }
}

/** "20 Aug" — compact enough for a chart axis. */
export function shortDayLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** Seconds as "2m 13s" / "48s". Null when there is nothing to average. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

/**
 * ISO-3166 alpha-2 to a country name. Falls back to the raw code when the browser has
 * no name for it, and passes "Unknown" through — that bucket is sessions whose
 * country could not be resolved, not a place.
 */
export function countryLabel(code: string): string {
  if (!code || code === "Unknown") return "Unknown";
  try {
    const names = new Intl.DisplayNames(undefined, { type: "region" });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}
